import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/shared/ToastNotification';
import { announcementAPI } from '../services/api';
import { track } from '../utils/analytics';

const STORAGE_KEY = 'fs_announcement_prompt_state_v1';
const DISPLAY_DELAY_MS = 32_000; // ~32 seconds after login
const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes polling fallback

const safeParse = (raw) => {
  try {
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
};

const readLocalState = () => {
  if (typeof window === 'undefined') return {};
  try {
    return safeParse(window.localStorage.getItem(STORAGE_KEY));
  } catch (_) {
    return {};
  }
};

const writeLocalState = (state) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (_) {
    // ignore storage errors (e.g. private mode)
  }
};

const hasHandled = (ann, record) => {
  if (!ann?.id) return false;
  const entry = record[ann.id];
  if (!entry) return false;
  if (ann.updated_at && entry.updatedAt && ann.updated_at !== entry.updatedAt) {
    return false;
  }
  return ['response', 'dismissed', 'acknowledged'].includes(entry.status);
};

export const useAnnouncementPrompt = ({ delayMs = DISPLAY_DELAY_MS, refreshMs = REFRESH_INTERVAL_MS } = {}) => {
  const { isAuthenticated, user } = useAuth();
  const { showError } = useToast();
  const token = user?.access_token;

  const [announcement, setAnnouncement] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThanks, setShowThanks] = useState(false);

  const timersRef = useRef({ ready: null, poll: null, autoClose: null });
  const fetchLockRef = useRef(false);
  const localStateRef = useRef(readLocalState());

  const clearTimer = useCallback((name) => {
    const handle = timersRef.current[name];
    if (handle) {
      clearTimeout(handle);
      clearInterval(handle);
      timersRef.current[name] = null;
    }
  }, []);

  const clearAllTimers = useCallback(() => {
    ['ready', 'poll', 'autoClose'].forEach(clearTimer);
  }, [clearTimer]);

  const updateLocalState = useCallback((ann, status) => {
    if (!ann?.id) return;
    localStateRef.current = {
      ...localStateRef.current,
      [ann.id]: {
        status,
        updatedAt: ann.updated_at || ann.created_at || null,
        recordedAt: Date.now(),
      },
    };
    writeLocalState(localStateRef.current);
  }, []);

  const closePrompt = useCallback(() => {
    clearTimer('autoClose');
    setIsOpen(false);
    setShowThanks(false);
    setAnnouncement(null);
  }, [clearTimer]);

  const handleDismiss = useCallback(async (reason = 'not_now') => {
    if (!announcement?.id) {
      closePrompt();
      return;
    }
    try {
      updateLocalState(announcement, 'dismissed');
      if (token) {
        await announcementAPI.submitResponse({
          token,
          announcementId: announcement.id,
          payload: {
            response_type: 'dismissed',
            metadata: { reason },
          },
        });
      }
      track('announcement_prompt_dismissed', {
        announcement_id: announcement.id,
        reason,
      });
    } catch (err) {
      console.error('Announcement dismiss failed', err);
    } finally {
      closePrompt();
    }
  }, [announcement, closePrompt, token, updateLocalState]);

  const handleSubmit = useCallback(async (message) => {
    if (!announcement?.id) return false;
    const trimmed = (message || '').trim();
    try {
      setIsSubmitting(true);
      await announcementAPI.submitResponse({
        token,
        announcementId: announcement.id,
        payload: {
          response_type: trimmed ? 'response' : 'acknowledged',
          message: trimmed || null,
        },
      });
      updateLocalState(announcement, trimmed ? 'response' : 'acknowledged');
      track('announcement_prompt_submitted', {
        announcement_id: announcement.id,
        with_message: Boolean(trimmed),
      });
      setShowThanks(true);
      return true;
    } catch (err) {
      console.error('Announcement submit failed', err);
      showError('Could not send your response. Please try again.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [announcement, showError, token, updateLocalState]);

  const handleOpenChange = useCallback((nextOpen) => {
    if (nextOpen) {
      setIsOpen(true);
      return;
    }
    if (showThanks) {
      closePrompt();
    } else {
      handleDismiss('modal_close');
    }
  }, [closePrompt, handleDismiss, showThanks]);

  const fetchActive = useCallback(async (reason = 'poll') => {
    if (!token || fetchLockRef.current) return;
    fetchLockRef.current = true;
    try {
      const data = await announcementAPI.fetchActive(token);
      const next = data?.announcement;
      if (next?.id) {
        if (hasHandled(next, localStateRef.current)) {
          return;
        }
        if (announcement?.id === next.id && isOpen) {
          return;
        }
        setAnnouncement(next);
        setShowThanks(false);
        setIsOpen(true);
        track('announcement_prompt_displayed', {
          announcement_id: next.id,
          audience: next.audience,
          reason,
        });
      }
    } catch (err) {
      console.error('Failed to fetch announcement prompt', err);
    } finally {
      fetchLockRef.current = false;
    }
  }, [announcement?.id, isOpen, token]);

  const scheduleTimers = useCallback(() => {
    clearTimer('ready');
    clearTimer('poll');
    if (!isAuthenticated || !token) {
      return;
    }
    timersRef.current.ready = window.setTimeout(() => {
      fetchActive('initial_delay');
    }, Math.max(1, delayMs));
    timersRef.current.poll = window.setInterval(() => {
      fetchActive('interval');
    }, Math.max(refreshMs, 60_000));
  }, [clearTimer, delayMs, fetchActive, isAuthenticated, refreshMs, token]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      clearAllTimers();
      closePrompt();
      localStateRef.current = readLocalState();
      return undefined;
    }

    localStateRef.current = readLocalState();
    scheduleTimers();
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers, closePrompt, isAuthenticated, scheduleTimers, token]);

  useEffect(() => {
    clearTimer('autoClose');
    if (!showThanks || !announcement?.auto_close_seconds || announcement.auto_close_seconds <= 0) {
      return undefined;
    }
    const timeoutMs = announcement.auto_close_seconds * 1000;
    timersRef.current.autoClose = window.setTimeout(() => {
      closePrompt();
    }, timeoutMs);
    return () => {
      clearTimer('autoClose');
    };
  }, [announcement, clearTimer, closePrompt, showThanks]);

  return {
    announcement,
    isOpen,
    isSubmitting,
    showThanks,
    setIsOpen,
    submitResponse: handleSubmit,
    dismissPrompt: handleDismiss,
    closePrompt,
    handleOpenChange,
  };
};

export default useAnnouncementPrompt;
